import { skipToken, useQuery } from '@tanstack/react-query';

import { Clips } from '@air/api';
import { type AssetSearchFilters, SortDirection } from '@air/api/types';
import { callAirApi } from '@air/utils-api';

import { WORKSPACE_AGENT_PROMPT_CARD_IMAGE_COUNT } from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentImprovedNullState';
import { PRIVATE_WORKSPACE_AGENT_ASSET_MENTION_CLIP_TYPES } from '~/components/v2/components/workspace/workspace-agent-panel/queries/usePrivateWorkspaceAgentAssetMentionSearch';

export const PRIVATE_WORKSPACE_AGENT_PROMPT_CARD_CLIPS_SEARCH_KEY = 'PRIVATE_WORKSPACE_AGENT_PROMPT_CARD_CLIPS_SEARCH';

export type PrivateWorkspaceAgentPromptCardClipsSearchParams = {
  filters: AssetSearchFilters;
  workspaceId: string | undefined;
};

export type PrivateWorkspaceAgentPromptCardClipsSearchKey = [
  typeof PRIVATE_WORKSPACE_AGENT_PROMPT_CARD_CLIPS_SEARCH_KEY,
  PrivateWorkspaceAgentPromptCardClipsSearchParams,
];

export const getPrivateWorkspaceAgentPromptCardClipsSearchKey = (
  params: PrivateWorkspaceAgentPromptCardClipsSearchParams,
): PrivateWorkspaceAgentPromptCardClipsSearchKey => [PRIVATE_WORKSPACE_AGENT_PROMPT_CARD_CLIPS_SEARCH_KEY, params];

/**
 * One page of `Clips.search` for a prompt card. Keyed apart from the gallery and the `@` picker
 * so those lists cannot share this page.
 */
export const usePrivateWorkspaceAgentPromptCardClipsSearch = (
  params: PrivateWorkspaceAgentPromptCardClipsSearchParams,
) => {
  const { filters, workspaceId } = params;

  return useQuery({
    queryKey: getPrivateWorkspaceAgentPromptCardClipsSearchKey(params),
    queryFn: workspaceId
      ? () =>
          callAirApi(() =>
            Clips.search({
              filters,
              limit: WORKSPACE_AGENT_PROMPT_CARD_IMAGE_COUNT,
              semantic: false,
              sortField: { direction: SortDirection.desc, name: 'dateModified' },
              types: PRIVATE_WORKSPACE_AGENT_ASSET_MENTION_CLIP_TYPES,
              workspaceId,
            }),
          )
      : skipToken,
    enabled: !!workspaceId,
  });
};
